import React, { useState } from 'react';
import ReportShell from './ReportShell';
import useReport from './useReport';

const SCREENS = [
  'athlete_dashboard', 'coach_dashboard', 'player_profile_view',
  'coach_needs_list', 'admin_dashboard', 'player_search',
  'card_builder_opened', 'coach_need_board_opened',
  'coach_need_matches_viewed',
];

/** Tint by share of taps. Dead and untracked controls each get their own
 *  outline entirely (no fill) -- both are findings, not background, and
 *  they must never look like each other: a dead control WAS instrumented
 *  and never touched, an untracked one was never instrumented at all. */
function fillFor(el) {
  if (el.state !== 'tapped') return 'transparent';
  const alpha = 0.15 + Math.min(el.share, 1) * 0.55;
  return `rgba(0, 102, 204, ${alpha})`;   // Constants.Colors.primary
}

function borderFor(el) {
  if (el.state === 'dead') return '2px dashed #b91c1c';        // instrumented, never touched
  if (el.state === 'untracked') return '2px dotted #6b7280';   // never instrumented at all
  return '1px solid rgba(0,102,204,0.9)';
}

/** Stable per-render key/testid. An untracked element carries `id: null` --
 *  there is no accessibility identifier to key it by -- so fall back to its
 *  index in the array. */
function keyFor(el, i) {
  return el.id ?? `untracked-${i}`;
}

export default function TapHeatmapReport() {
  // No population state. `population` is not a dimension of this report: the
  // tap-heatmap endpoint does not accept it, and FastAPI silently ignores an
  // unknown query param -- so rendering ReportShell's population <select> here
  // would give the reader a filter that visibly does nothing. These reports
  // exist because the old ones were untrusted for exactly that reason. Pass
  // showPopulation={false} and give useReport a fixed value it will not use.
  const [screenName, setScreenName] = useState(SCREENS[0]);
  const { data, loading, error, errorStatus } = useReport(
    'tap-heatmap', 'all', `&screen=${screenName}&days=30`,
  );

  // A 404 here means "nobody has captured and uploaded a reference map for
  // this screen yet" -- expected before any instrumented build ships, not a
  // failure. It must not render through ReportShell's error path, which
  // reads as broken. A genuine failure (500, network error, any other
  // status) still does.
  const noMapUploaded = errorStatus === 404;

  const elements = data?.elements || [];
  // "Never tapped" is a claim about controls that COULD have been tapped.
  // An untracked element never could -- no device can send an id it does
  // not have -- so it must not inflate either side of this ratio.
  const tracked = elements.filter((e) => e.tracked);
  const untracked = elements.filter((e) => !e.tracked);
  const deadCount = tracked.filter((e) => e.state === 'dead').length;
  const totalTaps = data?.total_taps ?? 0;
  // Every tracked control reads `state: 'dead'` when total_taps is 0 -- there
  // is no fourth "no data" state in the backend, and adding one here would
  // split the three-state model this report is built around. What must
  // change is the sentence: with zero taps recorded, "never tapped" implies
  // someone saw these controls and ignored them, when in fact nothing has
  // reported in at all. The rects still render as dead/red-dashed (that is
  // still an accurate description of the tap count), but the prose right
  // above them must say plainly that the data set is empty.
  const noTapsRecorded = !!data && totalTaps === 0;

  return (
    <ReportShell
      title="Tap Heatmap"
      headline={data ? `${data.total_taps} taps recorded on ${data.screen}.` : undefined}
      showPopulation={false}
      populationSize={data?.total_taps ?? 0}
      populationNoun="taps"
      loading={loading}
      error={noMapUploaded ? undefined : error}
    >
      <label>
        Screen:{' '}
        <select value={screenName} onChange={(e) => setScreenName(e.target.value)}>
          {SCREENS.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </label>

      {noMapUploaded && (
        <p className="report-note">
          No reference screen map has been uploaded for <strong>{screenName}</strong> yet, so
          there is nothing to draw here. Maps are captured from a simulator with{' '}
          <code>ios/ui-test-scripts/capture-screen-map.sh</code> and uploaded via{' '}
          <code>POST /api/admin/screen-maps</code>. Once one exists for this screen, this
          report will start drawing over it.
        </p>
      )}

      {data && (
        <>
          <p className="report-note">
            {noTapsRecorded ? (
              <>
                <strong>No taps have been recorded for this screen yet</strong> -- every
                control below shows as dead only because the data set is empty, not because
                anyone saw it and passed it by.
              </>
            ) : (
              <>
                <strong>{deadCount} of {tracked.length} instrumented controls were never
                tapped</strong> in the last 30 days.
              </>
            )}
            {' '}Geometry captured from app version {data.app_version}.
          </p>
          {untracked.length > 0 && (
            <p className="report-note">
              <strong>
                {untracked.length} control{untracked.length === 1 ? '' : 's'} on this screen
                {untracked.length === 1 ? ' carries' : ' carry'} no accessibility identifier
              </strong>
              {' '}and cannot be measured at all — shown below with a dotted outline, not
              counted in the sentence above.
            </p>
          )}

          <div className="report-tap-heatmap-layout">
            <div className="report-tap-heatmap-image" style={{ position: 'relative' }}>
              <img src={data?.image_url} alt={`${screenName} reference screenshot`}
                   style={{ display: 'block', width: '100%' }} />
              {elements.map((el, i) => (
                <div
                  key={keyFor(el, i)}
                  data-testid={`tap-rect-${keyFor(el, i)}`}
                  data-state={el.state}
                  title={
                    el.tracked
                      ? `${el.label || el.id} — ${el.taps} taps`
                      : `${el.label || 'untracked control'} — no accessibility identifier`
                  }
                  style={{
                    position: 'absolute',
                    left: `${el.x * 100}%`, top: `${el.y * 100}%`,
                    width: `${el.w * 100}%`, height: `${el.h * 100}%`,
                    background: fillFor(el),
                    // Dead and untracked controls are outlined, not filled: a
                    // shape you can see through is how "nothing happened here"
                    // should read.
                    border: borderFor(el),
                    boxSizing: 'border-box',
                  }}
                />
              ))}
            </div>

            <div className="report-table-wrap">
              <table className="report-table">
                <thead><tr><th>Control</th><th>Label</th><th>Taps</th><th>Share</th></tr></thead>
                <tbody>
                  {elements.map((el, i) => (
                    <tr key={keyFor(el, i)}>
                      <td><code>{el.id || '(untracked)'}</code></td>
                      <td>{el.label || '—'}</td>
                      <td>{el.tracked ? el.taps : '—'}</td>
                      <td>{el.tracked && el.taps ? `${Math.round(el.share * 100)}%` : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <h3>What this report cannot see</h3>
          <ul>
            <li>Controls with no <code>accessibilityIdentifier</code> are drawn with a distinct
                dotted outline (see above), not hidden -- they are <strong>untracked</strong>,
                which is not the same as <strong>dead</strong> (instrumented and never tapped).
                The two must never look alike.</li>
            <li>Rects are true for the scroll position at capture. Anything below the
                fold is positioned against the captured layout, not what the user saw.</li>
            <li>iOS taps only. Android sends the same identifiers but is recorded, not
                shown here -- the map above is captured from an iOS simulator, so an
                Android tap has nowhere accurate to be drawn.</li>
          </ul>
        </>
      )}
    </ReportShell>
  );
}
